import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Loader2, Save, Pencil, Cable } from 'lucide-react';
import { FormField, FormSelect, MultiSelectCheckboxes, OrangeCheckbox } from '../../shared/WizardComponents';

const PRODUCT_ROLES = ['Horizontal Cabling', 'Backbone Cabling', 'Fiber Optic Cabling', 'Copper Cabling', 'Mixed System'];
const SERVICE_TYPES = ['Data', 'Voice', 'Voice/Data', 'Security', 'AV', 'Controls'];
const APPLICABLE_STANDARDS = ['TIA', 'BICSI', 'NEC', 'ANSI', 'ISO/IEC', 'Local Code'];
const PERFORMANCE_CATEGORIES = ['Cat5e', 'Cat6', 'Cat6A', 'OM3', 'OM4', 'OS2'];

const EMPTY = {
  systemName: '', scopeDescription: '', productRole: '', serviceTypes: [],
  applicableStandards: [], minimumPerformanceCategory: '',
  testing: false, labeling: false, firestopping: false, qrEnabled: false, notes: '',
};

const normalize = (d) => ({
  systemName: d.systemName || '',
  scopeDescription: d.scopeDescription || '',
  productRole: d.productRole || '',
  serviceTypes: d.serviceTypes || [],
  applicableStandards: d.applicableStandards || [],
  minimumPerformanceCategory: d.minimumPerformanceCategory || '',
  testing: !!d.testing,
  labeling: !!d.labeling,
  firestopping: !!d.firestopping,
  qrEnabled: !!d.qrEnabled,
  notes: d.notes || '',
});

const SectionCard = ({ title, children }) => (
  <div className="bg-white rounded-lg border border-slate-200 px-5 py-5">
    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">{title}</h4>
    {children}
  </div>
);

const ReadField = ({ label, value }) => (
  <div className="space-y-1">
    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{label}</p>
    <p className="text-sm text-slate-800 min-h-[1.25rem]">
      {value !== '' && value !== null && value !== undefined ? value : <span className="text-slate-300">—</span>}
    </p>
  </div>
);

export const CablingInformationSection = ({ projectId, csiItem, canEditCSIItems }) => {
  const csiItemId = csiItem?.id;
  const [form, setForm] = useState(EMPTY);
  const [savedForm, setSavedForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchData = useCallback(async () => {
    if (!projectId || !csiItemId) return;
    try {
      const res = await axios.get(`${API}/communication-systems/${projectId}/structured-cabling/cabling-information?csi_item_id=${csiItemId}`);
      const d = res.data;
      if (d && Object.keys(d).length > 0) {
        const loaded = normalize(d);
        setForm(loaded);
        setSavedForm(loaded);
      }
    } catch {
      toast.error('Failed to load cabling information');
    } finally {
      setLoading(false);
    }
  }, [projectId, csiItemId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    const name = (form.systemName || '').trim();
    if (!name) e.systemName = 'System Name is required';
    else if (name.length < 3) e.systemName = 'Minimum 3 characters';
    else if (name.length > 100) e.systemName = 'Maximum 100 characters';
    if ((form.scopeDescription || '').length > 1000) e.scopeDescription = 'Maximum 1000 characters';
    if (!form.productRole) e.productRole = 'Product Role is required';
    if (!form.serviceTypes || form.serviceTypes.length < 1) e.serviceTypes = 'Select at least one Service Type';
    if (!form.minimumPerformanceCategory) e.minimumPerformanceCategory = 'Minimum Performance Category is required';
    if ((form.notes || '').length > 1000) e.notes = 'Maximum 1000 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) { toast.error('Please fix the highlighted fields'); return; }
    setSaving(true);
    try {
      const payload = { ...form, systemName: form.systemName.trim() };
      const res = await axios.put(
        `${API}/communication-systems/${projectId}/structured-cabling/cabling-information?csi_item_id=${csiItemId}`,
        payload,
      );
      const loaded = normalize(res.data);
      setForm(loaded);
      setSavedForm(loaded);
      setEditMode(false);
      toast.success('Cabling information saved');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save cabling information');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm(savedForm || EMPTY);
    setErrors({});
    setEditMode(false);
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const yn = (b) => (b ? 'Yes' : 'No');

  return (
    <div className="space-y-4" data-testid="comm-cabling-info-section">
      {/* Header / actions */}
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Cable className="h-4 w-4 text-orange-500" />Cabling Information
        </span>
        {canEditCSIItems && !editMode && (
          <Button size="sm" onClick={() => setEditMode(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="comm-cabling-info-edit-btn">
            <Pencil className="h-3.5 w-3.5 mr-1.5" />Edit
          </Button>
        )}
        {canEditCSIItems && editMode && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleCancel} data-testid="comm-cabling-info-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="comm-cabling-info-save-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
              Save
            </Button>
          </div>
        )}
      </div>

      {/* System Definition */}
      <SectionCard title="System Definition">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {editMode ? (
            <>
              <FormField label="System Name" required error={errors.systemName}>
                <Input
                  value={form.systemName}
                  onChange={(e) => set('systemName', e.target.value)}
                  maxLength={100}
                  placeholder="e.g. Main Building Data Cabling"
                  className={errors.systemName ? 'border-red-500' : ''}
                  data-testid="cabling-input-systemName"
                />
              </FormField>
              <FormField label="Product Role" required error={errors.productRole}>
                <FormSelect
                  value={form.productRole}
                  onValueChange={(v) => set('productRole', v)}
                  placeholder="Select product role"
                  options={PRODUCT_ROLES}
                  error={errors.productRole}
                  testId="cabling-select-productRole"
                />
              </FormField>
              <div className="md:col-span-2">
                <FormField label="Scope Description" error={errors.scopeDescription}>
                  <Textarea
                    value={form.scopeDescription}
                    onChange={(e) => set('scopeDescription', e.target.value)}
                    maxLength={1000}
                    rows={3}
                    placeholder="Describe the scope of this cabling system..."
                    className={errors.scopeDescription ? 'border-red-500' : ''}
                    data-testid="cabling-input-scopeDescription"
                  />
                </FormField>
              </div>
              <div className="md:col-span-2">
                {errors.serviceTypes && <p className="text-xs text-red-500 mb-1">{errors.serviceTypes}</p>}
                <MultiSelectCheckboxes
                  label="Service Type *"
                  options={SERVICE_TYPES}
                  selected={form.serviceTypes}
                  onChange={(v) => set('serviceTypes', v)}
                  testId="cabling-multi-serviceTypes"
                />
              </div>
            </>
          ) : (
            <>
              <ReadField label="System Name" value={form.systemName} />
              <ReadField label="Product Role" value={form.productRole} />
              <div className="md:col-span-2"><ReadField label="Scope Description" value={form.scopeDescription} /></div>
              <div className="md:col-span-2"><ReadField label="Service Type" value={(form.serviceTypes || []).join(', ')} /></div>
            </>
          )}
        </div>
      </SectionCard>

      {/* Standards & Performance */}
      <SectionCard title="Standards & Performance">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {editMode ? (
            <>
              <div className="md:col-span-2">
                <MultiSelectCheckboxes
                  label="Applicable Standards"
                  options={APPLICABLE_STANDARDS}
                  selected={form.applicableStandards}
                  onChange={(v) => set('applicableStandards', v)}
                  testId="cabling-multi-applicableStandards"
                />
              </div>
              <FormField label="Minimum Performance Category" required error={errors.minimumPerformanceCategory}>
                <FormSelect
                  value={form.minimumPerformanceCategory}
                  onValueChange={(v) => set('minimumPerformanceCategory', v)}
                  placeholder="Select category"
                  options={PERFORMANCE_CATEGORIES}
                  error={errors.minimumPerformanceCategory}
                  testId="cabling-select-minimumPerformanceCategory"
                />
              </FormField>
            </>
          ) : (
            <>
              <div className="md:col-span-2"><ReadField label="Applicable Standards" value={(form.applicableStandards || []).join(', ')} /></div>
              <ReadField label="Minimum Performance Category" value={form.minimumPerformanceCategory} />
            </>
          )}
        </div>
      </SectionCard>

      {/* Requirements */}
      <SectionCard title="Requirements">
        {editMode ? (
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            <OrangeCheckbox id="cabling-testing" checked={form.testing} onChange={() => set('testing', !form.testing)} label="Testing" testId="cabling-check-testing" />
            <OrangeCheckbox id="cabling-labeling" checked={form.labeling} onChange={() => set('labeling', !form.labeling)} label="Labeling" testId="cabling-check-labeling" />
            <OrangeCheckbox id="cabling-firestopping" checked={form.firestopping} onChange={() => set('firestopping', !form.firestopping)} label="Firestopping" testId="cabling-check-firestopping" />
            <OrangeCheckbox id="cabling-qrEnabled" checked={form.qrEnabled} onChange={() => set('qrEnabled', !form.qrEnabled)} label="QR Enabled" testId="cabling-check-qrEnabled" />
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <ReadField label="Testing" value={yn(form.testing)} />
            <ReadField label="Labeling" value={yn(form.labeling)} />
            <ReadField label="Firestopping" value={yn(form.firestopping)} />
            <ReadField label="QR Enabled" value={yn(form.qrEnabled)} />
          </div>
        )}
      </SectionCard>

      {/* Notes */}
      <SectionCard title="Notes">
        {editMode ? (
          <FormField label="Notes" error={errors.notes}>
            <Textarea
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              maxLength={1000}
              rows={3}
              placeholder="Additional notes..."
              className={errors.notes ? 'border-red-500' : ''}
              data-testid="cabling-input-notes"
            />
          </FormField>
        ) : (
          <ReadField label="Notes" value={form.notes} />
        )}
      </SectionCard>
    </div>
  );
};
